/**
 * store.ts —— 极简响应式 Store
 *
 * 不引框架的理由（design.md）：webcomponent 做解耦，PoC 规模下
 * 框架收益低于心智成本。这里只实现「读 / 写 / 订阅」三件事。
 */

type Listener<T> = (state: T, prev: T) => void;

export class Store<T extends object> {
  private state: T;
  private listeners = new Set<Listener<T>>();

  constructor(initial: T) {
    this.state = initial;
  }

  /** 读取当前状态（返回的是内部引用，请勿直接修改） */
  get(): Readonly<T> {
    return this.state;
  }

  /**
   * 合并写入。只在值真的变化时才通知订阅者，
   * 避免无意义的重复渲染。
   */
  set(patch: Partial<T>): void {
    const prev = this.state;
    let changed = false;

    for (const key of Object.keys(patch) as (keyof T)[]) {
      if (prev[key] !== patch[key]) {
        changed = true;
        break;
      }
    }
    if (!changed) return;

    this.state = { ...prev, ...patch };
    this.listeners.forEach((fn) => fn(this.state, prev));
  }

  /** 订阅变更，返回取消订阅函数 */
  subscribe(listener: Listener<T>): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}
